import { FC, RefObject, useState } from 'react';
import { useParams } from 'react-router-dom';
import { trpc } from 'src/trpc';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Loader } from 'src/components/Loader';
import { UserCog } from 'lucide-react';
import { CustomerCorrectionTab } from './CustomerCorrectionTab';

/**
 * The single entry point for changing which customer an order belongs to. The service address
 * is edited from the delivery edit sheet (the pencil next to Drop-off Address), which is the
 * single entry point for address changes - having two of them meant two different code paths
 * could write the same field.
 */
interface OrderCustomerCorrectionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Focus goes back here on close, so the keyboard lands where the drawer was opened from. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}

export const OrderCustomerCorrectionSheet: FC<OrderCustomerCorrectionSheetProps> = ({
  isOpen,
  onClose,
  restoreFocusRef,
}) => {
  const params = useParams();
  // Dismissing the drawer while a customer is being created or assigned would hide a write
  // the admin cannot then confirm or retry, so the drawer holds itself open until it settles.
  const [isBusy, setIsBusy] = useState(false);

  const { data, isLoading } = trpc.admin.order.adminGetOrderRelatedInfoById.useQuery(params.id!, {
    enabled: isOpen,
  });

  const order = data?.order;

  const requestClose = () => {
    if (!isBusy) onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && requestClose()}>
      <SheetContent
        side="right"
        className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full"
        onEscapeKeyDown={(event) => {
          if (isBusy) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isBusy) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          if (!restoreFocusRef?.current) return;
          event.preventDefault();
          restoreFocusRef.current.focus();
        }}
      >
        <div className="grid h-full grid-rows-[auto_1fr]">
          <SheetHeader className="border-b bg-white px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-orange-100 p-2">
                <UserCog className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <SheetTitle className="text-xl font-semibold text-gray-900">
                  Change order customer
                </SheetTitle>
                <SheetDescription className="text-sm text-gray-500">
                  Order #{order?.friendlyId}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="overflow-y-auto">
            {isLoading || !order ? (
              <div className="flex items-center justify-center h-full">
                <Loader title="Loading order details" />
              </div>
            ) : (
              <div className="p-6">
                <CustomerCorrectionTab
                  orderId={order._id!}
                  currentCustomer={data?.customer}
                  onCompleted={onClose}
                  onBusyChange={setIsBusy}
                />
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
